// Main trends analysis page component
// Handles the overall trends analysis workflow including:
// - Asset selection
// - Time range and window configuration
// - Data fetching and error handling
// - Rendering of charts and tables
import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from "react";
import { getTrendRateOfChange } from "@/api/api";
import { Header } from "@/components/header";
import { useSelectedAssets } from "@/contexts/SelectedAssetsContext";

import { SensorTrendTable } from "@/components/sensor-trend-table";
import { TrendAnalysisForm } from "@/components/trends/TrendAnalysisForm";
import { TrendChartSection } from "@/components/trends/TrendChartSection";
import { useThaloWeb } from "@/contexts/ThaloWebContext";
import useOnPeriods from "@/installation/onPeriods";
import { subDays, subWeeks, subMonths } from "date-fns";

interface DateRange {
  from: Date;
  to: Date;
}

// Route configuration for the trends beta page
export const Route = createFileRoute('/_authenticated/_staff/_with_location_switcher/trends-beta')({
  component: RouteComponent,
})

function RouteComponent() {
  // Asset selection state and configuration
  const { selectedAssetIds, selectedAssets, multipleAssetsSelected } = useSelectedAssets({ 
    selectFirstAssetByDefault: true,
    enforceSingleSelection: true 
  });
  const selectedInstallation = selectedAssets[0];

  // Analysis configuration state
  const [windowHours, setWindowHours] = useState<number>(24);
  const [rateOfChangeDenominator, setRateOfChangeDenominator] = useState<string>("day");
  const [timeRange, setTimeRange] = useState<string>("4d");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // UI and data state
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Record<string, any> | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Chart selection state for each chart type
  const [selectedSensor, setSelectedSensor] = useState<string>("");
  const [selectedMetric, setSelectedMetric] = useState<string>("");
  const [selectedPowerSensor, setSelectedPowerSensor] = useState<string>("");
  const [selectedPowerMetric, setSelectedPowerMetric] = useState<string>("");
  const [selectedDeltaSensor, setSelectedDeltaSensor] = useState<string>("");
  const [selectedDeltaMetric, setSelectedDeltaMetric] = useState<string>("");

  // State for managing trendlines across all chart types
  const [trendlines, setTrendlines] = useState<Array<{
    sensor: string;
    metric: string;
    data: any;
    chartType: 'temperature' | 'power' | 'delta';
  }>>([]);

  // Context and period tracking
  const { selectedOrganization, selectedLocation } = useThaloWeb();
  const [showOnTimes, setShowOnTimes] = useState(false);
  const assetsOnPeriods = useOnPeriods(selectedAssetIds);

  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 4),
    to: new Date(),
  });

  const handleDateRangeChange = (range: DateRange) => {
    if (range.from && range.to) {
      setDateRange(range);
      setWindowHours(
        Math.round(
          (range.to.getTime() - range.from.getTime()) / (1000 * 60 * 60)
        )
      );
    }
  };

  const handlePresetChange = (preset: string) => {
    const end = new Date();
    let start: Date;

    switch (preset) {
      case "4days":
        start = subDays(end, 4);
        break;
      case "1week":
        start = subWeeks(end, 1);
        break;
      case "2weeks":
        start = subWeeks(end, 2);
        break;
      case "1month":
        start = subMonths(end, 1);
        break;
      case "3months":
        start = subMonths(end, 3);
        break;
      case "custom":
        return;
      default:
        start = subDays(end, 4);
    }

    setDateRange({ from: start, to: end });
    setWindowHours(Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60)));
    
    // Update the start and end dates for the API
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const getTimeLabels = () => {
    const msDiff = dateRange.to.getTime() - dateRange.from.getTime();
    const windowMs = windowHours * 60 * 60 * 1000;

    // Guard against invalid windowHours or date range
    if (
      !isFinite(windowHours) ||
      windowHours <= 0 ||
      !isFinite(msDiff) ||
      msDiff < 0 ||
      !isFinite(windowMs) ||
      windowMs <= 0
    ) {
      return [];
    }

    // Limit the number of steps to a reasonable maximum (e.g., 1000)
    const maxSteps = 1000;
    const steps = Math.min(maxSteps, Math.max(1, Math.ceil(msDiff / windowMs) + 1));

    return Array.from({ length: steps }, (_, i) => {
      const date = new Date(dateRange.from.getTime() + i * windowMs);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        hour12: true,
      });
    });
  };

  const timeLabels = getTimeLabels();

  // Update dates when time range changes
  useEffect(() => {
    handlePresetChange(timeRange);
  }, [timeRange]);

  // Update URL when parameters change
  useEffect(() => {
    if (selectedInstallation?.id) {
      const params = new URLSearchParams(window.location.search);
      params.set('windowHours', windowHours.toString());
      params.set('rateOfChangeDenominator', rateOfChangeDenominator);
      params.set('timeRange', timeRange);
      params.set('startDate', startDate);
      params.set('endDate', endDate);
      params.set('assetId', selectedInstallation.id.toString());
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    }
  }, [windowHours, rateOfChangeDenominator, timeRange, startDate, endDate, selectedInstallation?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      console.log("Submitting with params:", {
        installation_id: selectedInstallation.id.toString(),
        start_date: startDate,
        end_date: endDate,
        window_hours: windowHours.toString(),
        rate_of_change_denominator: rateOfChangeDenominator,
      });

      const response = await getTrendRateOfChange({
        installation_id: selectedInstallation.id.toString(),
        start_date: startDate,
        end_date: endDate,
        window_hours: windowHours.toString(),
        rate_of_change_denominator: rateOfChangeDenominator,
      });

      console.log("API Response:", response);

      if (response.error) {
        if (response.error.includes("No data available")) {
          setError(`Error analyzing trend: ${response.error}\n\nNo data available for the selected time period. Please try a different date range or window hours.`);
        } else {
          setError(`Error analyzing trend: ${response.error}`);
        }
        return;
      }

      setResults(response);
    } catch (err: any) {
      console.error("Error details:", err);
      if (err.response?.data?.error === "No data available for the specified parameters") {
        setError(`Error details: ${JSON.stringify(err.response.data, null, 2)}\n\nNo data available for the selected time period. Please try a different date range or window hours.`);
      } else if (err.message?.includes("Incompatible merge dtype") || err.response?.status === 500) {
        setError(`Error details: ${err.message}\n\nNot enough data during this period to run analysis. Please try a different time range.`);
      } else {
        setError(`Error analyzing trend: ${err.message || "Unknown error"}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to get available sensors from results, filtered by chart type
  const getAvailableSensors = (chartType: 'temperature' | 'power' | 'delta') => {
    if (!results?.results) return [];
    return Object.keys(results.results)
      .map(sensor => {
        // Extract sensor name by removing the metric suffix
        const lastUnderscoreIndex = sensor.lastIndexOf('_');
        return sensor.substring(0, lastUnderscoreIndex);
      })
      .filter((value, index, self) => self.indexOf(value) === index)
      .filter(sensor => {
        // Filter sensors based on chart type
        const lowerSensor = sensor.toLowerCase();
        if (chartType === 'power') return lowerSensor.includes('power');
        if (chartType === 'delta') return lowerSensor.startsWith('delta_');
        return !lowerSensor.includes('power') && !lowerSensor.startsWith('delta_');
      });
  };

  // Function to get available metrics for a selected sensor
  const getAvailableMetrics = (chartType: 'temperature' | 'power' | 'delta') => {
    if (!results?.results) return [];
    
    // Get the correct sensor based on chart type
    let sensor = '';
    if (chartType === 'power') sensor = selectedPowerSensor;
    else if (chartType === 'delta') sensor = selectedDeltaSensor;
    else sensor = selectedSensor;
    
    if (!sensor) return [];
    
    // Get all keys that contain the sensor name
    const matchingKeys = Object.keys(results.results)
      .filter(key => key.includes(sensor));
    
    // Extract the metric (last part after underscore) from each matching key
    const metrics = matchingKeys
      .map(key => {
        const parts = key.split('_');
        return parts[parts.length - 1].toString();
      })
      .filter((value, index, self) => self.indexOf(value) === index);
    
    return metrics;
  };

  // Extract repeated trendline removal logic into a single function
  const removeTrendline = (index: number) => {
    setTrendlines(trendlines.filter((_, i) => i !== index));
  };

  // Extract repeated trendline addition logic into a single function
  const addTrendline = (sensor: string, metric: string, chartType: 'temperature' | 'power' | 'delta') => {
    if (sensor && metric && results?.results) {
      setTrendlines([...trendlines, {
        sensor,
        metric,
        data: results.results[`${sensor}_${metric}`],
        chartType
      }]);
    }
  };

  if (!selectedAssetIds || !selectedOrganization) {
    return <div>Loading</div>;
  }

  console.log('windowHours:', windowHours, 'dateRange:', dateRange);

  return (
    <div className="p-8">
      <div className="flex items-center gap-2">
        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">BETA</span>
        <Header name="Trends" showChartOptions={false} showDatePicker={false} />
      </div>
      {!selectedInstallation ? (
        <div className="text-red-500">Please select an installation to analyze.</div>
      ) : (
        <>
          <div className="mb-4">
            <strong>Selected Installation:</strong> {selectedInstallation.name}
          </div>
          <div className="mb-12">
            <TrendAnalysisForm
              timeRange={timeRange}
              setTimeRange={setTimeRange}
              windowHours={windowHours}
              setWindowHours={setWindowHours}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              rateOfChangeDenominator={rateOfChangeDenominator}
              setRateOfChangeDenominator={setRateOfChangeDenominator}
              loading={loading}
              onSubmit={handleSubmit}
              onCustomDateRangeChange={handleDateRangeChange}
            />
          </div>
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600">{error}</p>
            </div>
          )}
          {loading && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-blue-600">Analyzing trend data...</p>
            </div>
          )}
          {results && (
            <div className="grid grid-cols-1 gap-6">
              <SensorTrendTable 
                results={results} 
                rateOfChangeDenominator={rateOfChangeDenominator} 
              />
              <div className="space-y-12">
                <TrendChartSection
                  title="Temperature Chart"
                  chartType="temperature"
                  selectedSensor={selectedSensor}
                  setSelectedSensor={setSelectedSensor}
                  selectedMetric={selectedMetric}
                  setSelectedMetric={setSelectedMetric}
                  availableSensors={getAvailableSensors('temperature')}
                  availableMetrics={getAvailableMetrics('temperature')}
                  trendlines={trendlines}
                  onAddTrendline={() => addTrendline(selectedSensor, selectedMetric, 'temperature')}
                  onRemoveTrendline={removeTrendline}
                  selectedAssetIds={selectedAssetIds.map(String)}
                  startDate={startDate}
                  endDate={endDate}
                  results={results}
                />
                <TrendChartSection
                  title="Power Chart"
                  chartType="power"
                  selectedSensor={selectedPowerSensor}
                  setSelectedSensor={setSelectedPowerSensor}
                  selectedMetric={selectedPowerMetric}
                  setSelectedMetric={setSelectedPowerMetric}
                  availableSensors={getAvailableSensors('power')}
                  availableMetrics={getAvailableMetrics('power')}
                  trendlines={trendlines}
                  onAddTrendline={() => addTrendline(selectedPowerSensor, selectedPowerMetric, 'power')}
                  onRemoveTrendline={removeTrendline}
                  selectedAssetIds={selectedAssetIds.map(String)}
                  startDate={startDate}
                  endDate={endDate}
                  results={results}
                />
                <TrendChartSection
                  title="Delta Chart"
                  chartType="delta"
                  selectedSensor={selectedDeltaSensor}
                  setSelectedSensor={setSelectedDeltaSensor}
                  selectedMetric={selectedDeltaMetric}
                  setSelectedMetric={setSelectedDeltaMetric}
                  availableSensors={getAvailableSensors('delta')}
                  availableMetrics={getAvailableMetrics('delta')}
                  trendlines={trendlines}
                  onAddTrendline={() => addTrendline(selectedDeltaSensor, selectedDeltaMetric, 'delta')}
                  onRemoveTrendline={removeTrendline}
                  selectedAssetIds={selectedAssetIds.map(String)}
                  startDate={startDate}
                  endDate={endDate}
                  results={results}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
} 